import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { BadgePercent, Bell, Loader2, Ruler, Sparkles } from "lucide-react";
import { API } from "../lib/api";

const easeOut = [0.16, 1, 0.3, 1];

const PERKS = [
  { icon: BadgePercent, text: "10% off your very first order" },
  { icon: Bell, text: "Early access to new drops" },
  { icon: Ruler, text: "Size & fit tips from real parents" },
];

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      toast.success(res.data.message);
      setEmail("");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Something went wrong — please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 md:py-24" data-testid="newsletter-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: easeOut }}
          className="relative overflow-hidden rounded-[40px] bg-gradient-to-br from-blush-100 via-lav-50 to-baby-50 p-8 md:p-14"
        >
          <Sparkles className="absolute right-8 top-8 h-10 w-10 animate-twinkle text-lav-500/70" />
          <Sparkles className="absolute bottom-10 right-1/3 h-6 w-6 animate-twinkle text-blush-500/60" style={{ animationDelay: "0.9s" }} />

          <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-blush-700">
                The Spark Club
              </p>
              <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
                Join the Spark Club and get 10% off
              </h2>
              <p className="mt-4 max-w-md leading-relaxed text-muted">
                Be first to new drops, sizing tips and sparkly surprises —
                straight to your inbox, never spammy.
              </p>
              <ul className="mt-6 space-y-3">
                {PERKS.map((perk) => (
                  <li key={perk.text} className="flex items-center gap-3 text-sm font-semibold text-ink/80">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/80">
                      <perk.icon className="h-4 w-4 text-blush-700" />
                    </span>
                    {perk.text}
                  </li>
                ))}
              </ul>
            </div>

            <form
              onSubmit={submit}
              data-testid="newsletter-form"
              className="rounded-[28px] bg-white/90 p-7 shadow-xl shadow-lav-100 backdrop-blur md:p-9"
            >
              <label
                htmlFor="newsletter-email"
                className="font-display text-lg font-semibold text-ink"
              >
                Grab your 10% off code
              </label>
              <input
                id="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="parent@example.com"
                data-testid="newsletter-email-input"
                className="mt-4 w-full rounded-full border border-lav-100 bg-cream px-5 py-3.5 text-sm text-ink outline-none transition-colors duration-200 placeholder:text-muted/60 focus:border-blush-500"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-subscribe-button"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink py-4 font-display text-base font-semibold text-white shadow-lg shadow-ink/15 transition-transform duration-300 hover:scale-[1.02] disabled:opacity-70"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Sending sparkle..." : "Subscribe"}
              </button>
              <p className="mt-3 text-center text-xs text-muted">
                One quick email a week, unsubscribe anytime.
              </p>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
